import { SettingsManager, Utils } from "@viamap/viamap2-common";
import React from "react";
import { MapitUtils } from "src/managers/MapitUtils";
import { FeatureAllowDenyListMap } from "src/states/ApplicationStateFeatureMapping";
import { JobTypePersistence } from "./JobDataStore";

export enum MessageType {
    Error = "Error",
    Success = "Success"
}

export enum ProcesStatus {
    Start = "Start",
    ServitutArealValgt = "ServitutArealValgt",
    ErstatningsArealValgt = "ErstatningsArealValgt",
    ErstatningBeregnet = "ErstatningBeregnet"
}

export enum JobTypeUserAccessRights {
    "Read" = "Read",
    "Write" = "Write",
    "Admin" = "Admin"
}

export enum InputFieldType {
    SelectLayer,
    SelectColumn,
    Settings,
    Title,
};



/**
* User Access Rights to Project Access Parameters
*/
export const FeaturesAccessibleJobsUserAccessRights: FeatureAllowDenyListMap = {
    [JobTypeUserAccessRights.Read]: {
        allow: ["Read"],
        deny: ["Write", "Admin"]
    },
    [JobTypeUserAccessRights.Write]: {
        allow: ["Read", "Write"],
        deny: ["Admin"]
    },
    [JobTypeUserAccessRights.Admin]: {
        allow: ["Read", "Write", "Admin"],
        deny: []
    },
};

export enum BNBOLodsejerStatus {
    "Ny" = "Ny",
    "Under udarbejdelse" = "Under udarbejdelse",
    "Klar" = "Klar",
    "Sendt til høring" = "Sendt til høring",
    "Høring udløbet" = "Høring udløbet",
    "Tinglyst" = "Tinglyst",
    "Afvist" = "Afvist",
    "Accepteret" = "Accepteret",
    "Godkendt" = "Godkendt",
}

export interface JobTypePersistenceInterface {
    listObjects(filter: any): Promise<JobType[]>;
    getObject(objectId: string): Promise<JobType>;
    saveObject(object: JobType): Promise<void>;
    deleteObject(objectId: string): Promise<void>;
    createObject(object: JobType): Promise<void>
};


export type TurfFeature = any; // for documentation only.
export type geoJsonMultipolygon = any;

export enum JobStatus {
    "NotStarted" = "NotStarted",
    "InProgress" = "InProgress",
    "Paused" = "Paused",
    "Completed" = "Completed",
    "CompletedWithErrors" = "CompletedWithErrors",
    "Failed" = "Failed"
}

export interface JobType {
    id: string;
    name: string;
    description: string;
    active: boolean;
    serverTypeName?: string;
    ownerUser: string; // User id of the owner. E.g. kbe@viamap.net
    ownerOrganization: string; // Organization of the owner. E.g. viamap.net
    allowedUsersWithRoles: { [userId: string]: string[] }; // E.g. {"kbe@viamap.net":["BNBO","BNBOAdmin","BNBOWriteAcess"],"mbj@molbak.dk":["BNBO","BNBOWriteAcess"]}
    allowedOrganizationsWithRoles: { [organizationId: string]: string[] }; // E.g. {"viamap.net":["BNBO"],"molbak.dk":["BNBO", "BNBOWriteAcess"]}
    settings: any;
    _version?: number; // Version number of the document when read from the data store
    protected: boolean; // If true, the project is protected from deletion or bulk update
}

export interface JobInstance {
    id: string;
    jobTypeId: string;
    jobStatus: JobStatus;
    createdByUser: string;
    createdByOrganization: string;

    settings: any;
    active: boolean;
    esIndexName: string;
    _version?: number; // Version number of the document when read from the data store

    // 
    startedAt?: Date;
    completedAt?: Date;
    state: JobState;
    expectedCompletion?: Date; // Expected completion time
    expectedDuration?: number; // Expected duration in seconds
    percentCompleted?: number; // 0-100
}

// ---------------------------------------------------- STATE --------------------------------------------------------------

export interface JobState {
    stateVersion: 1,
    activeTransactions: { [guid: string]: any },
    message: string,
    messageType: MessageType,

    isInitialized: boolean;

    availableJobTypesList: JobType[];
    selectedJobTypeId?: string;
    selectedJobType?: JobType;

    instancesList: JobInstance[];
}

// ---------------------------------------------------- INITIAL STATE --------------------------------------------------------------

export function initialJobState(): JobState {

    return {
        stateVersion: 1, //Increment, when requiredFields are added
        activeTransactions: {},
        message: "",
        messageType: MessageType.Success,

        isInitialized: false,

        availableJobTypesList: [],
        instancesList: []
    };
}

export type SFEElem = { addr: string, sfeNummer: number, status: string }

// ---------------------------------------------------- ACTIONS --------------------------------------------------------------
export enum JobActionType {
    TxStarted,
    TxCompleted,
    TxFailed,

    Initialize,

    AddJobType,
    UpdateJobType,
    DeleteJobType,
    SelectJobType,
    LoadJobTypes,

    InstantiateJob,
}

export interface TxStarted {
    type: JobActionType.TxStarted;
    payload: { guid: string, action: JobActions };
}

export interface TxCompleted {
    type: JobActionType.TxCompleted;
    payload: { guid: string, action: JobActions };
}

export interface TxFailed {
    type: JobActionType.TxFailed;
    payload: { guid: string, action: JobActions, error: any };
}


export interface Initialize {
    type: JobActionType.Initialize;
    payload: {
        jobTypeElasticIndex: string,
        userId: string
    };
    results?: {
        jobTypes: JobType[];
    };
}


export interface AddJobType {
    type: JobActionType.AddJobType
    payload: { id: string, item: JobType }
}
export interface UpdateJobType {
    type: JobActionType.UpdateJobType
    payload: { id: string, item: JobType, versionNumberWhenRead?: number }
}
export interface DeleteJobType {
    type: JobActionType.DeleteJobType
    payload: { id: string, versionNumberWhenRead?: number }
}
export interface SelectJobType {
    type: JobActionType.SelectJobType
    payload: { id: string }
}
export interface LoadJobTypes {
    type: JobActionType.LoadJobTypes;
    results?: any; // loaded data
    callback?: (availableJobTypesList: JobType[]) => void;
}

export interface InstantiateJob {
    type: JobActionType.InstantiateJob;
    payload: {
        jobType: JobType,
        data: { [tag: string]: any },
        settings: any,
    };
    results?: { jobId: string };
    callback?: (jobId: string) => void;
}

// -------------------- utility functions to create an Action object ---------------------------------

export const actionTxStarted = (guid: string, action: JobActions): TxStarted => ({
    type: JobActionType.TxStarted,
    payload: { guid, action }
});

export const actionTxCompleted = (guid: string, action: JobActions): TxCompleted => ({
    type: JobActionType.TxCompleted,
    payload: { guid, action }
});

export const actionTxFailed = (guid: string, action: JobActions, error: any): TxFailed => ({
    type: JobActionType.TxFailed,
    payload: { guid, action, error }
});

export const initialize = (jobTypeElasticIndex: string, userId: string): Initialize => ({
    type: JobActionType.Initialize,
    payload: { jobTypeElasticIndex, userId }
});

export type JobActionsTransActional = Initialize | LoadJobTypes | AddJobType | UpdateJobType | DeleteJobType | SelectJobType;
export type JobActionsNonTransActional = TxStarted | TxCompleted | TxFailed;
export type JobActions = JobActionsTransActional | JobActionsNonTransActional;

// ---------------------------------------------------- ACTIONS --------------------------------------------------------------



// ---------------------------------------------------- TRANSACTIONAL REDUCER --------------------------------------------------------------

export function transactionalJobsReducer(action: JobActionsTransActional, dispatch: any) {

    switch (action.type) {

        case JobActionType.Initialize: {
            if (action.payload.jobTypeElasticIndex) {
                let projectsPersistence = new JobTypePersistence(action.payload.jobTypeElasticIndex, action.payload.userId);
                JobTypePersistence.setInstance(projectsPersistence);
                let txId = MapitUtils.getNewUuid();
                dispatch(actionTxStarted(txId, action));
                JobTypePersistence.getInstance().listObjects()
                    .then((projects) => {
                        action.results = { jobTypes: projects };
                        dispatch(actionTxCompleted(txId, action));
                        dispatch(action);
                    })
                    .catch((error) => {
                        console.log("Got error " + error);
                        dispatch(actionTxFailed(txId, action, "Error get projects: " + (error.message || error)));
                    })
            }

            break;
        }

        case JobActionType.LoadJobTypes: {
            let txId = MapitUtils.getNewUuid();
            dispatch(actionTxStarted(txId, action));
            let projectsPersistence = new JobTypePersistence(SettingsManager.getSystemSetting("jobs.jobTypeElasticIndex", ""), "dummy, only for loading");
            JobTypePersistence.setInstance(projectsPersistence);
            JobTypePersistence.getInstance().listObjects().then((result) => {
                dispatch(actionTxCompleted(txId, action));
                action.results = result;
                dispatch(action);
            }).catch((error) => {
                console.log("Got error " + error);
                dispatch(actionTxFailed(txId, action, "Error load: " + (error.message || error)));
            })
            break;
        }

        default:
            // proceed directly to non-transational reducer for other actions
            dispatch(action);
    }
}


// ---------------------------------------------------- REDUCER --------------------------------------------------------------

export function safeAddToDoubleDepthObject(topObject: { [key1: string]: { [key2: string]: any } }, key1: string, key2: string, value: any) {
    if (!topObject[key1]) {
        topObject[key1] = {};
    }
    if (!topObject[key1][key2]) {
        topObject[key1][key2] = {};
    }
    topObject[key1][key2] = value;
}

export function safeGetDoubleDepthObject(topObject: { [key1: string]: { [key2: string]: any } }, key1: string, key2: string): any {
    if (!topObject[key1]) {
        return null;
    }
    if (!topObject[key1][key2]) {
        return null;
    }
    return topObject[key1][key2];
}

export function JobsReducer(state: JobState, action: JobActions): JobState {


    switch (action.type) {

        case JobActionType.TxStarted:
            return {
                ...state,
                activeTransactions: { ...state.activeTransactions, [action.payload.guid]: action }
            };

        case JobActionType.TxFailed: {
            let tmp = { ...state.activeTransactions };
            delete tmp[action.payload.guid];
            console.log(action.payload.error);
            let msg = Utils.formatString(/*Localization.getTextSafeMode(*/"Error. Transaction {txname} returned with {error}"/*)*/, {
                txname: /*Localization.getTextSafeMode(*/JobActionType[action.payload.action.type]/*)*/,
                error: /*Localization.getTextSafeMode(*/action.payload.error/*)*/
            });
            if ((action.payload.action as any).errorCallback) {
                (action.payload.action as any).errorCallback(action.payload.error);
            }
            return {
                ...state,
                message: msg,
                messageType: MessageType.Error,
                activeTransactions: tmp
            };
        }

        case JobActionType.TxCompleted: {
            let tmp = { ...state.activeTransactions };
            delete tmp[action.payload.guid];
            return {
                ...state,
                activeTransactions: tmp,
                message: ""
            };
        }

        case JobActionType.Initialize: {
            return {
                ...state,
                availableJobTypesList: action.results ? action.results.jobTypes : [],
                isInitialized: true
            };
            return state;
        }

        case JobActionType.SelectJobType: {
            let project = (state.availableJobTypesList || []).find((p) => p.id === action.payload.id);
            if (project) {
                return { ...state, selectedJobType: project, selectedJobTypeId: action.payload.id }
            } else {
                return state;
            }
        }

        case JobActionType.LoadJobTypes: {
            if (action.callback) {
                action.callback(action.results);
            };
            return { ...state, availableJobTypesList: action.results }
        }

        case JobActionType.UpdateJobType: {
            return {
                ...state,
                selectedJobType: action.payload.id === state.selectedJobTypeId ? action.payload.item : state.selectedJobType,
                availableJobTypesList: (state.availableJobTypesList || []).map((p) => p.id === action.payload.id ? action.payload.item : p)
            }
        }

        case JobActionType.AddJobType: {
            return { ...state, availableJobTypesList: (state.availableJobTypesList || []).concat([action.payload.item]) }
        }

        case JobActionType.DeleteJobType: {
            return { ...state, availableJobTypesList: (state.availableJobTypesList || []).filter((p) => p.id !== action.payload.id) }
        }

        default:
            throw new Error("Unknown action " + (action as JobActions).type);
    }
}

// ---------------------------------------------------- generic stuff --------------------------------------------------------------

export const JobsContext = React.createContext<{
    state: JobState;
    dispatch: React.Dispatch<JobActions>;
}>({
    state: initialJobState(),
    dispatch: () => undefined,
});
JobsContext.displayName = "JobsContext";

export class GenericTransactionManager {

    static dispatchMiddleware<ActionTypes>(dispatch: any, transactionalReducer: (action: any, dispatch: any) => any) {

        return (action: ActionTypes) => {
            transactionalReducer(action, dispatch);
        };

    }
}

export function getMyAccessRights(project: JobType, userRef: string, customerRef: string): string[] | undefined {
    let accessRights = project.allowedUsersWithRoles && project.allowedUsersWithRoles[userRef] || project.allowedOrganizationsWithRoles && project.allowedOrganizationsWithRoles[customerRef];
    if (accessRights && accessRights.length == 1) {
        // For now only a single access right is supported
        let myAllowDeny = FeaturesAccessibleJobsUserAccessRights[accessRights[0]];
        if (!myAllowDeny) {
            console.error("No access rights found for this project");
            return;
        }
        for (let feature of myAllowDeny.allow) {
            accessRights.push(feature.toString());
        }
        for (let feature of myAllowDeny.deny) {
            accessRights = accessRights.filter((val) => val !== feature.toString());
        }
    } else {
        console.error("No access rights found for this project");
    }

    return accessRights;
}


